import { useForm } from 'react-hook-form';
import { dataToForm, OtherIncomeFormValues, UpsertIncomeDTO } from './OtherIncomeForm.meta';
import { IncomeModel, IncomeType } from '../../api/useIncomeQuery';
import { useUpsertIncomeMutation } from '../../api/useUpsertIncomeMutation';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { ADD_INCOME_SUCCESS_MESSAGE, EDIT_INCOME_SUCCESS_MESSAGE } from '../../Income.meta';
import { IncomeData } from '../../useIncome';

const FIELDS_TO_SHOW_ADVANCED = ['StartAge', 'EndAge', 'Inflation', 'IsTaxFree'];

interface Params {
  clientId: string;
  income: IncomeModel | null;
  data: IncomeData;
  onSuccess: () => void;
}

export const useOtherIncomeForm = ({ clientId, income, data, onSuccess }: Params) => {
  const formBag = useForm<OtherIncomeFormValues>({
    defaultValues: dataToForm(income, data),
  });

  const [managementStyleValue, customBucketIndex] = formBag.watch([
    'orionManagementStyleId',
    'customBucketIndex',
  ]);

  const mutationBag = useUpsertIncomeMutation({ clientId });

  const defaultShowAdvanced =
    !!income &&
    (FIELDS_TO_SHOW_ADVANCED.some(field => !!income[field] || income[field] === 0) ||
      income.AccountEarmark.length > 0);

  const { Options: options } = data;

  const startYearOptions = [
    { value: null, label: 'Start Today' },
    ...options.StartYearOptions.map(year => ({
      value: year,
      label: year,
    })),
  ];

  const endYearOptions = [
    { value: -1, label: 'End of Plan' },
    { value: null, label: 'At Retirement' },
    ...options.OtherIncomeEndYearOptions.map(year => ({
      value: year,
      label: year,
    })),
  ];

  const earnerOptions = Object.keys(options.OwnerOptions).map(key => ({
    label: key,
    options: (options.OwnerOptions[key] || []).map((owner, ind) => ({
      value: owner.Name,
      label: owner.Name,
    })),
  }));

  const onSubmit = formBag.handleSubmit(async (values: OtherIncomeFormValues) => {
    const body: UpsertIncomeDTO = {
      clientId,
      ...values,
      endAge: values.endAtRetirement || values.endAtEndOfPlan ? null : values.endAge,
      id: income?.Id || null,
      incomeType: IncomeType.Other,
      AccountEarmark: values.accountEarmarks.map(earmark => ({ Type: earmark })),
    };

    try {
      await mutationBag.mutateAsync(body);

      toast.success(income?.Id ? EDIT_INCOME_SUCCESS_MESSAGE : ADD_INCOME_SUCCESS_MESSAGE);

      onSuccess();
    } catch (error) {
      console.error(error);
    }
  });

  return {
    data: {
      formBag,
      defaultShowAdvanced,
      earnerOptions,
      startYearOptions,
      endYearOptions,
      mutationBag,
      options,
      managementStyleValue,
      customBucketIndex,
    },
    actions: {
      onSubmit,
    },
  };
};
